// ** React Imports
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// ** MUI Imports
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { toast, ToastContainer } from "react-toastify";

// ** Custom Component Imports
import CustomTextField from "components/mui/text-field";

// ** API Imports
import { saveUnit } from "../logic/UnitofMeasurementlogic";

const UnitDialog = ({ open, handleClose, UnitProps }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    existingUnit,
    fetchUnits,
    unitContent,
    onUnitCreated,
  } = UnitProps;

  const [unit, setUnit] = useState({
    unitID: 0,
    unitName: "",
    unitSymbol: "", 
    isActive: true,
  });

  const [errors, setErrors] = useState({
    unitName: false,
    unitNameMsg: '',
    unitSymbol: false, 
    unitSymbolMsg: '', 
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (unitContent.entity === 'create') {
      resetUnit();
    }

    if (unitContent.entity === 'edit' && existingUnit) {
      setUnit({
        unitID: existingUnit.unitID || 0,
        unitName: existingUnit.unitName || "",
        unitSymbol: existingUnit.unitSymbol || "", 
        isActive: existingUnit.isActive !== undefined ? existingUnit.isActive : true,
      });
    }
  }, [open, existingUnit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUnit((prevUnit) => ({
      ...prevUnit,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateUnit = () => {
    const unitNameError = validateUnitName(unit.unitName);
    const unitSymbolError = validateUnitSymbol(unit.unitSymbol); 

    const newErrors = {
      unitName: unitNameError.error,
      unitNameMsg: unitNameError.message,
      unitSymbol: unitSymbolError.error, 
      unitSymbolMsg: unitSymbolError.message, 
    };

    setErrors(newErrors);

    return !unitNameError.error; 
  };

  const validateUnitName = (unitName) => {
    if (!unitName?.trim()) {
      return { error: true, message: 'Unit Name is required' };
    }

    if (unitName.trim().length < 2) {
      return { error: true, message: 'Unit Name must be at least 2 characters long' };
    }

    return { error: false, message: '' };
  };

  const validateUnitSymbol = (unitSymbol) => {
    
    if (unitSymbol && unitSymbol.trim().length > 10) {
      return { error: true, message: 'Unit Symbol must be less than 10 characters' };
    }

    return { error: false, message: '' };
  };

const handleSave = async () => {
  const isValid = validateUnit();

  if (!isValid) {
    toast.error("Please fill out all required fields correctly", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
    return;
  }

  setLoading(true);

  try {
    const result = await saveUnit(unit, navigate);
    
    if (result && !result.isError) {
      
      const isEdit = unitContent?.entity === 'edit';
      const message = isEdit 
        ? "Unit Updated Successfully" 
        : "Unit Added Successfully";
      
      toast.success(message, {
        style: {
          fontFamily: '"DM Sans", sans-serif',
          fontSize: '15px',
        },
      });
      
      if (fetchUnits) {
        await fetchUnits();
      }
      
      if (onUnitCreated) {
        onUnitCreated(result);
      }
      
      resetUnit();
      handleClose();
    } else {
      toast.error(result?.message || "Failed to save unit", {
        style: {
          fontFamily: '"DM Sans", sans-serif',
          fontSize: '15px',
        },
      });
    }
  } catch (error) {
    console.error("Error saving unit:", error);
    toast.error("An error occurred while saving the unit", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
  } finally {
    setLoading(false);
  }
};

  const resetUnit = () => {
    setUnit({
      unitID: 0,
      unitName: "",
      unitSymbol: "", 
      isActive: true,
    });
    setErrors({
      unitName: false,
      unitNameMsg: '',
      unitSymbol: false, 
      unitSymbolMsg: '', 
    });
  };

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth="xs"
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid #D9D9D9",
            paddingBottom: "20px !important",
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {unitContent?.title || "Add Unit of Measure"}
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={5}>
            <Grid item xs={12}>
              <CustomTextField
                fullWidth
                required
                label="Unit Name"
                name="unitName"
                value={unit.unitName}
                placeholder="Enter unit name (e.g., Kilogram, Meter)"
                onChange={handleChange}
                error={errors.unitName}
                helperText={errors.unitName ? errors.unitNameMsg : ''}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12}>
              <CustomTextField
                fullWidth
                label="Unit Symbol"
                name="unitSymbol" 
                value={unit.unitSymbol} 
                placeholder="Enter unit symbol (e.g., kg, m) - Optional"
                onChange={handleChange}
                error={errors.unitSymbol} 
                disabled={loading}
              />
            </Grid>
            
            {/* <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="isActive"
                    checked={unit.isActive}
                    onChange={handleChange}
                    color="primary"
                    disabled={loading}
                  />
                }
                label="Active Unit"
                sx={{
                  '& .MuiFormControlLabel-label': {
                    color: theme.palette.text.primary,
                  }
                }}
              />
            </Grid> */}
          </Grid>
        </DialogContent>
        
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            padding: "20px 24px !important",
            boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
            zIndex: 1,
          }}
          className="dialog-actions-dense"
        >
          <Box>
            <Button 
              variant="contained" 
              color="info" 
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
          </Box>
          <Box>
            <Button 
              variant="contained" 
              color="main" 
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? "Saving..." : (unitContent?.entity === 'edit' ? 'Save Changes' : 'Save Unit')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UnitDialog;